'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Logo } from '#pplx/frontend/aether/components/Logo/index.js';
import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { ComputerAffiliateBind } from '#pplx/frontend/apps/ask/analytics/ComputerAffiliateBindLoader.js';
import { HomeMobileOverlayShell, useMobileSearchOverlay, } from '#pplx/frontend/apps/ask/ask-composer/homeMobileOverlay.js';
import { AskInputProvider } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProvider.js';
import { useAskInputActions, useAskInputState, } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProviderShared.js';
import { useEnterprisePostCheckoutSetupTrigger } from '#pplx/frontend/apps/ask/billing/components/useEnterprisePostCheckoutSetupTrigger.js';
import { isMaybeIncognitoComet } from '#pplx/frontend/apps/ask/comet-shared/cometUtils.js';
import { appDownloadButtonClickedEvent } from '#pplx/frontend/apps/ask/events/app-download/analytics.js';
import { useBrandedVisitorGate } from '#pplx/frontend/apps/ask/hooks/branded-visitor-gate-hooks/useBrandedVisitorGate.js';
import { useAllowedFloatingUpsellPath } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/useAllowedFloatingUpsellPath.js';
import { IncognitoInputHint } from '#pplx/frontend/apps/ask/incognito/IncognitoInputHint.js';
import { useIsIncognito } from '#pplx/frontend/apps/ask/incognito/useIsIncognito.js';
import { MobileSidebarButton } from '#pplx/frontend/apps/ask/layout/header/MobileSidebarButton.js';
import * as AppLayout from '#pplx/frontend/apps/ask/layout/page/AppLayoutV2.js';
import { useLaunchEnterpriseComputerOnboardingV2 } from '#pplx/frontend/apps/ask/onboarding-computer/v2/useLaunchEnterpriseComputerOnboardingV2.js';
import { useOrgAdminMessage } from '#pplx/frontend/apps/ask/organization/hooks/useOrgAdminMessage.js';
import { useOrgRefreshSubscription } from '#pplx/frontend/apps/ask/organization/hooks/useOrgRefreshSubscription.js';
import { useCachedHomeComposerInputVariation } from '#pplx/frontend/apps/ask/page-components/home/homeComposerInputFlag.js';
import { HomeInput } from '#pplx/frontend/apps/ask/page-components/home/HomeInput.js';
import { getHomeWidgetsEligibility } from '#pplx/frontend/apps/ask/page-components/home/internal/homeWidgetsEligibility.js';
import { shouldReservePendingHomePrimarySurface, shouldRedirectCometHomeToHome, shouldRedirectCometHomeToMobileNtp, shouldShowCometWidgets, } from '#pplx/frontend/apps/ask/page-components/home/internal/resolveHomePrimarySurface.js';
import { LegacyHomeInput } from '#pplx/frontend/apps/ask/page-components/home/LegacyHomeInput.js';
import { useHomepageQueryParamsEffect } from '#pplx/frontend/apps/ask/page-components/home/useHomepageQueryParamsEffect.js';
import { useResultsActions } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { clearHistorySearchParams } from '#pplx/frontend/apps/ask/routing/historySearchParams.js';
import { isNeoOnboardingPending } from '#pplx/frontend/apps/ask/routing/onboarding.js';
import { isCometLandingPage } from '#pplx/frontend/apps/ask/routing/routes.js';
import { routes } from '#pplx/frontend/apps/ask/routing/spaRoutes.js';
import { useUrlModalHandler } from '#pplx/frontend/apps/ask/runtime/useUrlModalHandler.js';
import { useShouldShowSplashScreen } from '#pplx/frontend/apps/ask/signup-ui/useShouldShowSplashScreen.js';
import { useInputActions } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { useShowKoreanCompliance } from '#pplx/frontend/apps/ask/subscription/hooks/useShowKoreanCompliance.js';
import { PageBottomSlot } from '#pplx/frontend/apps/ask/upsell-slots/comet-ntp-bottom/PageBottomSlot.js';
import { HomepageTopBannerUpsellSlot } from '#pplx/frontend/apps/ask/upsell-slots/homepage-top-banner/HomepageTopBannerUpsellSlot.js';
import { useWindowsLegacyAppUpsellModalPlan } from '#pplx/frontend/apps/ask/upsell-slots/homepage-top-banner/useWindowsLegacyAppUpsellModalPlan.js';
import { WindowsLegacyAppUpsellModalSlot } from '#pplx/frontend/apps/ask/upsell-slots/homepage-top-banner/WindowsLegacyAppUpsellModalSlot.js';
import { OmitUpsellIf } from '#pplx/frontend/apps/ask/upsells/components/OmitUpsellIf.js';
import { useCometFullScreenUpsell } from '#pplx/frontend/apps/ask/upsells/hooks/useCometFullScreenUpsell.js';
import { useLocalUserSettings } from '#pplx/frontend/apps/ask/user-settings/getLocalUserSettings.js';
import { HomepageComponentsProvider } from '#pplx/frontend/apps/ask/widgets/home-primer-tabs/HomepageComponentsProvider.js';
import { HomepageWidgets } from '#pplx/frontend/apps/ask/widgets/home/HomepageWidgets.js';
import { useIsEnterpriseUser } from '#pplx/frontend/auth/useIsEnterpriseUser.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { isCometBrowser as getIsCometBrowser, isCometMobileBrowser, } from '#pplx/frontend/comet/client/utils.js';
import { useClientRender } from '#pplx/frontend/common/hooks/useClientRender.js';
import LegacyButton from '#pplx/frontend/components/buttons/Button.js';
import Toast from '#pplx/frontend/components/overlays/Toast.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { LOGIN_NEW_PARAM, RECURRING_GATE_IMPRESSIONS_LIMIT, } from '#pplx/frontend/components/utils/consts.js';
import { RedirectReason } from '#pplx/frontend/components/utils/PrivacyUtils.js';
import { getHasLoggedIn, getHasUserQueries } from '#pplx/frontend/cookies/metadataCookieManager.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useSingularAttributionAppLink } from '#pplx/frontend/growth/analytics-singular/useSingularAttributionAppLink.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { redirectWindow } from '#pplx/frontend/platform-components/redirectWindow.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import classNames from 'classnames';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
const PrivacyBadge = Dynamic(async () => {
    const { PrivacyBadge } = await import('#pplx/frontend/apps/ask/upsells/components/PrivacyBadge.js');
    return { default: PrivacyBadge };
});
const HomepageProBadgeUpsell = Dynamic(async () => {
    const { HomepageProBadgeUpsell } = await import('#pplx/frontend/apps/ask/page-components/HomepageProBadgeUpsell.js');
    return { default: HomepageProBadgeUpsell };
});
const Interstitial = Dynamic(async () => {
    const { Interstitial } = await import('#pplx/frontend/apps/ask/signup-ui/Interstitial.js');
    return { default: Interstitial };
});
const AdminTips = Dynamic(async () => {
    const { AdminTips } = await import('#pplx/frontend/apps/ask/page-components/AdminTips.js');
    return { default: AdminTips };
});
const KoreanCompliancePopover = Dynamic(async () => {
    const { KoreanCompliancePopover } = await import('#pplx/frontend/apps/ask/subscription/components/KoreanCompliancePopover.js');
    return { default: KoreanCompliancePopover };
});
const KoreanComplianceFooter = Dynamic(async () => {
    const { KoreanComplianceFooter } = await import('#pplx/frontend/apps/ask/subscription/components/KoreanComplianceFooter.js');
    return { default: KoreanComplianceFooter };
});
const ModalUpsellSlot = Dynamic(async () => {
    const { ModalUpsellSlot } = await import('#pplx/frontend/apps/ask/upsell-slots/modal/ModalUpsellSlot.js');
    return { default: ModalUpsellSlot };
});
const HomeTopRightControls = Dynamic(async () => {
    const { HomeTopRightControls } = await import('#pplx/frontend/apps/ask/page-components/HomeTopRightControls.js');
    return { default: HomeTopRightControls };
});
const NarrowHomeIncognitoControl = Dynamic(async () => {
    const { NarrowHomeIncognitoControl } = await import('#pplx/frontend/apps/ask/page-components/HomeTopRightControls.js');
    return { default: NarrowHomeIncognitoControl };
});
const DifferentiatedHomepageTitle = Dynamic(async () => {
    const { DifferentiatedHomepageTitle } = await import('#pplx/frontend/apps/ask/page-components/internal/DifferentiatedHomepageTitle.js');
    return { default: DifferentiatedHomepageTitle };
});
const OrgInviteModal = Dynamic(async () => {
    const { OrgInviteModal } = await import('#pplx/frontend/apps/ask/organization/components/invite/OrgInviteModal.js');
    return { default: OrgInviteModal };
});
const OneTimeJoinVerifiedOrg = Dynamic(async () => {
    const { OneTimeJoinVerifiedOrg } = await import('#pplx/frontend/apps/ask/onboarding/components/OneTimeJoinVerifiedOrg.js');
    return { default: OneTimeJoinVerifiedOrg };
});
const REASONS_MESSAGES = defineMessages({
    threadAccessNotAllowed: {
        defaultMessage: 'Cannot view this session.',
        id: 'cf1a627eb1',
        description: 'Error message shown when user lacks permission to view a thread',
    },
    threadExpired: {
        defaultMessage: 'This session is expired and cannot be viewed.',
        id: '3b43939332',
        description: 'Error message shown when attempting to view an expired thread',
    },
    invalidThread: {
        defaultMessage: 'This session does not exist.',
        id: '400e3761e1',
        description: 'Error message shown when thread ID is invalid or not found',
    },
    threadStreamError: {
        defaultMessage: 'Unable to start session.',
        id: '3eda7e983e',
        description: 'Error message shown when thread streaming fails',
    },
    pageAccessNotAllowed: {
        defaultMessage: 'Cannot view this page.',
        id: 'a49c6b7875',
        description: 'Error message shown when user lacks permission to view a page',
    },
    spaceAccessNotAllowed: {
        defaultMessage: 'Cannot view this Space.',
        id: '126b6f370f',
        description: 'Error message shown when user lacks permission to view a Space',
    },
    subscriptionAlreadyActive: {
        defaultMessage: 'Unable to apply discount code to an active subscription.',
        id: '18644985ef',
        description: 'Error message shown when trying to apply discount to active subscription',
    },
    unableToGrantAccess: {
        defaultMessage: 'Unable to make this session shareable, ensure you are logged in as the owner of the session.',
        id: 'c2e28e2a7d',
        description: 'Error message shown when non-owner tries to share a thread',
    },
    collectionAccessNotAllowed: {
        defaultMessage: 'Cannot view this collection.',
        id: 'e2d42da3c7',
        description: 'Error message shown when user lacks permission to view a collection',
    },
    invalidCollection: {
        defaultMessage: 'This collection does not exist.',
        id: '3dbd1f08f9',
        description: 'Error message shown when collection ID is invalid or not found',
    },
    invalidTask: {
        defaultMessage: 'This session does not exist.',
        id: 'fa1595ba69',
        description: 'Error message shown when task ID is invalid or not found',
    },
    invalidAsset: {
        defaultMessage: 'This artifact does not exist.',
        id: '1167ce4b27',
        description: 'Error shown when a requested file-like item in the Computer asset viewer does not exist. "Asset" is not a financial asset.',
    },
});
// Object.hasOwn, not `in`: the reason comes from an attacker-shareable query
// param, and `in` walks the prototype chain, so `?error=toString` would pass
// the guard and crash $t on a prototype method instead of a message.
const isHomeRedirectReason = (reason) => Object.hasOwn(REASONS_MESSAGES, reason);
const HOME_TOP_CHROME_ICON_BUTTON_UNDERLAY_CLASS = 'inline-flex empty:hidden rounded-full bg-base';
const HOME_TOP_CHROME_PILL_BUTTON_UNDERLAY_CLASS = 'rounded-full bg-base';
/** Selects HomeInput or the legacy overlay on mobile web. */
export function MobileSearchInput() {
    const shouldUseHomeComposer = useCachedHomeComposerInputVariation({ defaultValue: false });
    const recessedFrame = !import.meta.env.VITE_IS_SIDECAR_BUILD;
    if (shouldUseHomeComposer) {
        return _jsx(HomeInput, { recessedFrame: recessedFrame });
    }
    return _jsx(LegacyMobileSearchInput, { recessedFrame: recessedFrame });
}
/**
 * Gives legacy touch handlers access to the input ref inside AskInputProvider.
 */
function useInputElementBridge(gettersRef) {
    const { inputRef } = useAskInputState();
    const { isMobileStyle, isMobileUserAgent } = useIsMobile();
    // Update the ref after render so concurrent renders cannot change it early.
    useEffect(() => {
        gettersRef.current = {
            containsElement: (node) => inputRef.current?.element()?.contains(node) ?? false,
            mwebFocusRoot: (opts) => {
                if (!isMobileStyle || !isMobileUserAgent) {
                    logger.warn('mwebFocusRoot should only be called on mobile web');
                }
                inputRef.current?.mwebFocusRoot(opts);
            },
        };
    }, [inputRef, gettersRef, isMobileStyle, isMobileUserAgent]);
}
function LegacyMobileSearchInput({ recessedFrame }) {
    const askInputRef = useRef(null);
    const inputGettersRef = useRef(null);
    const { setUserInput } = useInputActions();
    const { setSuggestions, setShowSuggestDropdown } = useAskInputActions();
    useInputElementBridge(inputGettersRef);
    const { isOverlayActive, closeOverlay, overlayHandlers } = useMobileSearchOverlay({
        onRefocus: () => askInputRef.current?.focusInput(),
        // Focus immediately so Safari opens the keyboard without scrolling.
        onTouchFocus: () => inputGettersRef.current?.mwebFocusRoot({ preventScroll: true }),
        getIsEditableTarget: (target) => (target instanceof Node && inputGettersRef.current?.containsElement(target)) ?? false,
    });
    const onBack = useCallback(() => {
        setUserInput('');
        setSuggestions([]);
        setShowSuggestDropdown(false);
        closeOverlay();
    }, [closeOverlay, setShowSuggestDropdown, setSuggestions, setUserInput]);
    return (_jsx(HomeMobileOverlayShell, { isActive: isOverlayActive, recessedFrame: recessedFrame, onBack: onBack, overlayHandlers: overlayHandlers, children: _jsx(LegacyHomeInput, { ref: askInputRef, recessedFrame: recessedFrame }) }));
}
/**
 * Desktop / sidecar search input with standard positioning.
 * Handles input positioning, incognito hint, and homepage widgets.
 */
const DesktopSearchInput = React.memo(function DesktopSearchInput({ desktopSupplementaryContent, hasBottomUpsellSlot, inputRef, isHomepageWidgetsDisabled, isIncognito, initialModeResolved, onPrimarySurfaceContentChange, showCometWidgets, }) {
    const { isMobileStyle } = useIsMobile();
    const recessedFrame = !import.meta.env.VITE_IS_SIDECAR_BUILD;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { ref: inputRef, className: classNames('w-full', {
                    '-mt-[5px]': recessedFrame,
                    'relative z-30': !isMobileStyle && !import.meta.env.VITE_IS_SIDECAR_BUILD,
                    'absolute bottom-4': !!import.meta.env.VITE_IS_SIDECAR_BUILD || isMobileStyle,
                    'bottom-4 mb-4 animate-in fade-in fill-mode-both slide-in-from-bottom absolute delay-200 duration-300 ease-in-out': !!import.meta.env.VITE_IS_SIDECAR_BUILD,
                }), children: [_jsx(HomeInput, { recessedFrame: recessedFrame }), _jsx(IncognitoInputHint, { className: "mt-4 hidden md:flex" })] }), desktopSupplementaryContent, _jsx(HomeWidgets, { composerRef: inputRef, hasBottomUpsellSlot: hasBottomUpsellSlot, isHomepageWidgetsDisabled: isHomepageWidgetsDisabled, isIncognito: isIncognito, isMobileStyle: isMobileStyle, initialModeResolved: initialModeResolved, onPrimarySurfaceContentChange: onPrimarySurfaceContentChange, recessedFrame: recessedFrame, showCometWidgets: showCometWidgets })] }));
});
const HomeWidgets = React.memo(function HomeWidgets({ composerRef, hasBottomUpsellSlot, isHomepageWidgetsDisabled, isIncognito, isMobileStyle, initialModeResolved, onPrimarySurfaceContentChange, recessedFrame, showCometWidgets, }) {
    const areHomeWidgetsEligible = getHomeWidgetsEligibility({
        areHomepageWidgetsDisabled: isHomepageWidgetsDisabled,
        initialModeResolved,
        isIncognito,
        isMobileStyle,
    });
    useEffect(() => {
        if (!areHomeWidgetsEligible) {
            onPrimarySurfaceContentChange({ hasContent: false, hasResolved: true });
        }
    }, [areHomeWidgetsEligible, onPrimarySurfaceContentChange]);
    if (!areHomeWidgetsEligible)
        return null;
    return (_jsx("div", { className: "relative mt-8 w-full", children: _jsx(HomepageWidgets, { composerRef: composerRef, flushOneLineCards: recessedFrame, hasBottomUpsellSlot: hasBottomUpsellSlot, isIncognito: isIncognito, onPrimarySurfaceContentChange: onPrimarySurfaceContentChange, showCometWidgets: showCometWidgets }) }));
});
const HomeUpsellSideEffects = React.memo(function HomeSideEffects() {
    const { $t } = useIntl();
    const isUserLoggedIn = useIsUserLoggedIn();
    const router = useRouter();
    const { isEnterpriseMax, subscriptionTier } = useIsSubscribed();
    const launchEnterpriseComputerOnboardingV2 = useLaunchEnterpriseComputerOnboardingV2();
    const { env: { isLocalhost }, } = useWebPlatform();
    const { isMobileUserAgent } = useIsMobile();
    const { openModal } = useModalContext();
    const query = useSearchParams();
    const isCometBrowser = isMaybeIncognitoComet();
    const hasMounted = useClientRender();
    const [isNeoAskCompletionFlowActive, setIsNeoAskCompletionFlowActive] = useState(false);
    useEffect(() => {
        if (isNeoOnboardingPending()) {
            setIsNeoAskCompletionFlowActive(true);
        }
    }, []);
    useCometFullScreenUpsell();
    const { openVisitorLoginUpsell } = useBrandedVisitorGate({
        enabled: true,
    });
    const [welcomeBackGateImpressions, setWelcomeBackGateImpressions] = useLocalUserSettings('welcomeBackGateImpressions', 0);
    const hasShownWelcomeBackGate = useRef(false);
    // For handling modals that are triggered by URL params
    useUrlModalHandler({ confirmationRedirectsToHomepage: true });
    // only runs for enterprise checkout on stripe
    useOrgRefreshSubscription({ reason: 'search-inner-home' });
    const completeResumedEnterpriseOnboarding = useCallback((closeCurrentModal) => {
        closeCurrentModal();
        router.push(routes['/computer'].href());
    }, [router]);
    const handleResumedEnterpriseComputerOnboardingV2Handoff = useCallback(() => launchEnterpriseComputerOnboardingV2({
        completeEnterpriseOnboarding: completeResumedEnterpriseOnboarding,
        metadata: {
            entrypoint: 'enterprise_admin_creation',
            subscriptionTier: subscriptionTier ?? (isEnterpriseMax ? 'max' : 'pro'),
        },
    }), [
        completeResumedEnterpriseOnboarding,
        isEnterpriseMax,
        launchEnterpriseComputerOnboardingV2,
        subscriptionTier,
    ]);
    useEnterprisePostCheckoutSetupTrigger(handleResumedEnterpriseComputerOnboardingV2Handoff);
    useHomepageQueryParamsEffect();
    // Auto-open org invite modal when URL params present
    useEffect(() => {
        const inviteUUID = query?.get('org_invite');
        const openInviteLinkUUID = query?.get('open_invite_link');
        if (inviteUUID || openInviteLinkUUID) {
            const isNewUser = query?.get(LOGIN_NEW_PARAM) === 'true';
            if (inviteUUID) {
                openModal(OrgInviteModal, {
                    invitationUUID: inviteUUID,
                    isNewUser,
                }, { replace: true });
            }
            else if (openInviteLinkUUID) {
                openModal(OrgInviteModal, {
                    openInviteLinkUUID,
                    isNewUser,
                }, { replace: true });
            }
        }
    }, [query, openModal]);
    const isOrgInviteFlowActive = Boolean(query?.has('org_invite') || query?.has('open_invite_link'));
    const isSubscriptionSuccessFlowActive = query?.get('stripe_checkout') === 'success' || query?.get('upgrade_max') === 'success';
    const windowsLegacyAppUpsellModalPlan = useWindowsLegacyAppUpsellModalPlan(hasMounted &&
        !isNeoAskCompletionFlowActive &&
        !isOrgInviteFlowActive &&
        !isSubscriptionSuccessFlowActive);
    useEffect(() => {
        if (hasShownWelcomeBackGate.current ||
            welcomeBackGateImpressions >= RECURRING_GATE_IMPRESSIONS_LIMIT ||
            isCometBrowser ||
            isUserLoggedIn ||
            isMobileUserAgent ||
            isLocalhost ||
            windowsLegacyAppUpsellModalPlan.show) {
            return;
        }
        if (getHasLoggedIn() && getHasUserQueries()) {
            openVisitorLoginUpsell({
                origin: ModalOrigin.WELCOME_BACK,
                title: $t({
                    defaultMessage: 'Welcome back!',
                    id: 'b65c216fc8',
                    description: 'Title for login modal shown to returning users',
                }),
                description: $t({
                    defaultMessage: 'Log in to continue your journey.',
                    id: '97683fa5d4',
                    description: 'Description for login modal shown to returning users',
                }),
                sheetModalVariant: 'centered-sheet',
            });
            hasShownWelcomeBackGate.current = true;
            setWelcomeBackGateImpressions((n) => n + 1);
        }
    }, [
        isCometBrowser,
        isLocalhost,
        isMobileUserAgent,
        isUserLoggedIn,
        openModal,
        $t,
        welcomeBackGateImpressions,
        setWelcomeBackGateImpressions,
        openVisitorLoginUpsell,
        windowsLegacyAppUpsellModalPlan.show,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(WindowsLegacyAppUpsellModalSlot, { plan: windowsLegacyAppUpsellModalPlan }), _jsx(ModalUpsellSlot, { source: {
                    source: 'homepage',
                    responseChannel: 'upsell_information',
                    request: {},
                }, callsite: {
                    enabled: !isNeoAskCompletionFlowActive && !windowsLegacyAppUpsellModalPlan.show,
                    routeKind: 'home',
                    surface: {
                        isMounted: hasMounted,
                        isOrgInviteFlowActive,
                        isSubscriptionSuccessFlowActive,
                    },
                } })] }));
});
export const Home = React.memo(function InnerHome({ desktopSupplementaryContent }) {
    const { $t } = useIntl();
    const { hasAccessToProFeatures } = useIsSubscribed();
    const { device: { isIOS }, } = useWebPlatform();
    const { isMobileUserAgent, isMobileStyle } = useIsMobile();
    const { clear: clearResults } = useResultsActions();
    const [errorMessage, setErrorMessage] = useState(null);
    const { track } = useInstrumentation();
    const query = useSearchParams();
    const { handleAppDownloadClicked } = useSingularAttributionAppLink({
        origin: 'mobile-header',
    });
    const errorQueryParam = useMemo(() => query?.get('error'), [query]);
    const isEnterprise = useIsEnterpriseUser();
    // Clear the state so that we don't see old results when a new search starts
    useEffect(() => {
        clearResults();
    }, [clearResults]);
    useEffect(() => {
        // Show error toast and remove from sidebar if URL param contains error
        if (errorQueryParam) {
            // Untrusted URL content must not reach retained logs; record only
            // allowlisted reasons.
            logger.info('Error redirect', {
                errorReason: isHomeRedirectReason(errorQueryParam) ? errorQueryParam : 'unknown',
            });
            if (isHomeRedirectReason(errorQueryParam)) {
                setErrorMessage($t(REASONS_MESSAGES[errorQueryParam]));
            }
            // clear error query params
            clearHistorySearchParams();
        }
    }, [errorQueryParam, $t]);
    const onClick = useCallback(() => {
        track(appDownloadButtonClickedEvent({
            name: 'app download button clicked',
            data: {
                origin: 'mobile-header',
            },
        }));
        handleAppDownloadClicked();
    }, [handleAppDownloadClicked, track]);
    const initialValue = useMemo(() => ({
        homepageTitle: $t({
            defaultMessage: 'What do you want to know?',
            id: 'f9b3201e64',
            description: 'Main heading displayed on the homepage',
        }),
    }), [$t]);
    const [isHomepageWidgetsDisabled] = useLocalUserSettings('isHomepageWidgetsDisabled', false);
    const hideWidgets = query?.get('hide') === 'true';
    const areHomepageWidgetsDisabled = isHomepageWidgetsDisabled || hideWidgets;
    const shouldShowSplashScreen = useShouldShowSplashScreen();
    const appButtonIcon = useMemo(() => {
        return isIOS ? $icon('custom-brand-apple') : $icon('brand-android');
    }, [isIOS]);
    const showKoreanCompliance = useShowKoreanCompliance();
    const adminMessage = useOrgAdminMessage();
    const pathname = usePathname();
    const router = useRouter();
    const desktopSearchInputRef = useRef(null);
    const [hasBottomUpsellSlot, setHasBottomUpsellSlot] = useState(false);
    const [homePrimarySurfaceContent, setHomePrimarySurfaceContent] = useState({
        hasContent: false,
        hasResolved: false,
        key: '',
    });
    const { initialConfiguredModelIsLoading } = useThreadConfig();
    const hasMounted = useClientRender();
    const initialModeResolved = hasMounted && !initialConfiguredModelIsLoading;
    const isAllowedFloatingUpsellPath = useAllowedFloatingUpsellPath();
    const { isIncognito } = useIsIncognito({ reason: 'home-widgets' });
    const isMobileWeb = isMobileStyle && isMobileUserAgent;
    const areHomeWidgetsEligible = getHomeWidgetsEligibility({
        areHomepageWidgetsDisabled,
        initialModeResolved,
        isIncognito,
        isMobileStyle,
    });
    const isCometHomePath = Boolean(isCometLandingPage(pathname));
    const isComet = getIsCometBrowser();
    const isCometRouteOwner = isMaybeIncognitoComet();
    const isCometMobile = isCometMobileBrowser();
    useLayoutEffect(() => {
        if (shouldRedirectCometHomeToMobileNtp({ isCometHomePath, isCometMobile })) {
            redirectWindow('/new-tab-page', 'comet-mobile-home-page');
            return;
        }
        if (!shouldRedirectCometHomeToHome({ isCometHomePath, isCometRouteOwner }))
            return;
        router.replace('/', 'comet-home-page');
    }, [isCometHomePath, isCometMobile, isCometRouteOwner, router]);
    // Comet makes the homepage widget grid eligible on desktop.
    const showCometWidgets = shouldShowCometWidgets({
        isComet: isComet || isCometRouteOwner,
        isMobileStyle,
        areHomeWidgetsEligible,
    });
    const homePrimarySurfaceContentKey = [
        areHomeWidgetsEligible,
        isComet || isCometRouteOwner,
        isMobileStyle,
        showCometWidgets,
    ].join(':');
    const currentHomePrimarySurfaceContent = homePrimarySurfaceContent.key === homePrimarySurfaceContentKey
        ? homePrimarySurfaceContent
        : { hasContent: false, hasResolved: false, key: homePrimarySurfaceContentKey };
    const handleHomePrimarySurfaceContentChange = useCallback(({ hasContent, hasResolved }) => {
        setHomePrimarySurfaceContent({
            hasContent,
            hasResolved,
            key: homePrimarySurfaceContentKey,
        });
    }, [homePrimarySurfaceContentKey]);
    const hasPageBottomPrimarySurfaceContent = currentHomePrimarySurfaceContent.hasContent ||
        shouldReservePendingHomePrimarySurface({
            areHomeWidgetsEligible,
            hasPrimarySurfaceContentResolved: currentHomePrimarySurfaceContent.hasResolved,
            isComet: isComet || isCometRouteOwner,
            isMobileStyle,
        });
    return (_jsxs(_Fragment, { children: [_jsx(HomeUpsellSideEffects, {}), _jsx(ComputerAffiliateBind, {}), _jsx(OneTimeJoinVerifiedOrg, {}), _jsx("div", { className: "pointer-events-none absolute inset-x-0 top-0 z-10 hidden h-24 bg-linear-to-b from-base to-transparent md:block" }), _jsx("div", { className: "pointer-events-none px-4 inset-x-0 z-30 absolute flex justify-center top-0", children: _jsx(HomepageTopBannerUpsellSlot, { callsite: { enabled: initialModeResolved, routeKind: 'home' } }) }), _jsxs("div", { className: "top-0 h-headerHeight left-4 z-20 absolute hidden items-center gap-2 md:flex", children: [isEnterprise && (_jsx("div", { className: classNames('animate-fadeIn duration-300', HOME_TOP_CHROME_PILL_BUTTON_UNDERLAY_CLASS), children: _jsx(PrivacyBadge, { compact: true }) })), _jsx(HomepageProBadgeUpsell, { className: HOME_TOP_CHROME_PILL_BUTTON_UNDERLAY_CLASS })] }), _jsx(HomeTopRightControls, { initialModeResolved: initialModeResolved, controlWrapperClassName: HOME_TOP_CHROME_ICON_BUTTON_UNDERLAY_CLASS }), _jsx(AppLayout.Content, { childrenWidth: import.meta.env.VITE_IS_SIDECAR_BUILD ? 'none' : 'default', mobileHeader: false, shouldUseSurface: false, children: _jsxs("div", { className: "erp-sidecar:px-4 sm:px-4 md:px-8 isolate mx-auto size-full sm:max-w-screen-md", children: [_jsxs("div", { className: "relative flex h-full flex-col", children: [_jsxs(BoxPrimitive, { className: classNames('py-4 pr-4 pl-1 h-headerHeight flex items-center justify-between border-b', 
                                    // Negative margin to break out of parent's sm:px-4 so toolbar goes edge-to-edge
                                    '-mx-4', 'md:hidden'), children: [_jsxs("div", { className: "gap-x-1 flex items-center", children: [_jsx(MobileSidebarButton, {}), _jsx(Logo, { color: "mixed", isPro: hasAccessToProFeatures, size: "small" })] }), _jsxs("div", { className: "gap-x-2 flex items-center", children: [showKoreanCompliance && _jsx(KoreanCompliancePopover, {}), _jsx(NarrowHomeIncognitoControl, { initialModeResolved: initialModeResolved, controlWrapperClassName: HOME_TOP_CHROME_ICON_BUTTON_UNDERLAY_CLASS }), isMobileUserAgent && (_jsx(OmitUpsellIf, { isSamsungBrowser: true, isComet: true, isFirefoxDefaultSearch: true, children: _jsx("div", { className: "animate-in fade-in duration-300", children: _jsx(LegacyButton, { size: "small", icon: appButtonIcon, text: $t({
                                                                defaultMessage: 'Open in App',
                                                                id: 'facd4a39dc',
                                                                description: 'Button to open Perplexity mobile app',
                                                            }), pill: true, variant: "primary", onClick: onClick }) }) }))] })] }), _jsx(HomepageComponentsProvider, { initialValue: initialValue, children: _jsxs(BoxPrimitive, { className: "static w-full grow flex-col items-center pb-[10vh] md:mt-0 md:flex z-10", children: [_jsx("div", { className: "hidden shrink-0 md:block md:h-[40vh]" }), _jsx("div", { className: "px-4 relative flex size-full flex-col justify-center md:h-auto md:px-0", children: _jsx(DifferentiatedHomepageTitle, {}) }), _jsx(AskInputProvider, { children: isMobileWeb ? (_jsx(MobileSearchInput, {})) : (_jsx(DesktopSearchInput, { desktopSupplementaryContent: desktopSupplementaryContent, hasBottomUpsellSlot: hasBottomUpsellSlot, inputRef: desktopSearchInputRef, isHomepageWidgetsDisabled: areHomepageWidgetsDisabled, isIncognito: isIncognito, initialModeResolved: initialModeResolved, onPrimarySurfaceContentChange: handleHomePrimarySurfaceContentChange, showCometWidgets: showCometWidgets })) }), _jsxs("div", { className: "pointer-events-none absolute inset-x-0 bottom-0 hidden min-h-14 items-center justify-center gap-2 pb-2 md:flex", children: [!isMobileStyle && adminMessage.message && (_jsx(AdminTips, { ...adminMessage.message, hasBottomUpsellSlot: hasBottomUpsellSlot })), showKoreanCompliance && _jsx(KoreanComplianceFooter, {})] }), _jsx(PageBottomSlot, { callsite: {
                                                    enabled: initialModeResolved && isAllowedFloatingUpsellPath,
                                                    pathname,
                                                    routeKind: 'home',
                                                    surface: {
                                                        hasPrimarySurfaceContent: hasPageBottomPrimarySurfaceContent,
                                                        isAllowedFloatingPath: isAllowedFloatingUpsellPath,
                                                        isMobileLayout: isMobileStyle,
                                                    },
                                                }, inputRef: desktopSearchInputRef, onVisibilityChange: setHasBottomUpsellSlot })] }) })] }), _jsx(Toast, { isVisible: errorMessage != null, variant: "error", message: errorMessage, timeout: 7 })] }) }), !import.meta.env.VITE_IS_SIDECAR_BUILD && shouldShowSplashScreen && (_jsx(OmitUpsellIf, { isSamsungBrowser: true, isFirefoxDefaultSearch: true, isPartnershipCampaign: true, isGovernment: true, children: _jsx(Interstitial, {}) }))] }));
});
//# sourceMappingURL=index.js.map